/**
 * Format an RFC 3339 timestamp as a short, locale-aware Chinese display.
 * Returns "—" if the input is empty or unparseable.
 */
export function formatTimestamp(rfc3339: string | null | undefined): string {
  if (!rfc3339) return '—';
  const date = new Date(rfc3339);
  if (Number.isNaN(date.getTime())) return rfc3339;
  const pad = (n: number) => String(n).padStart(2, '0');
  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const hh = pad(date.getHours());
  const mi = pad(date.getMinutes());
  const ss = pad(date.getSeconds());
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
}

/**
 * Format a duration in milliseconds as a short Chinese string.
 * Examples: 12 → "12ms"; 1500 → "1.5s"; 95000 → "1m35s"; 3700000 → "1h1m".
 */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  if (m < 60) return s > 0 ? `${m}m${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm > 0 ? `${h}h${mm}m` : `${h}h`;
}

/** JSON.stringify with 2-space indent and circular-reference fallback. */
export function safeJsonStringify(value: unknown, indent = 2): string {
  const seen = new WeakSet<object>();
  return JSON.stringify(
    value,
    (_key, val) => {
      if (typeof val === 'object' && val !== null) {
        if (seen.has(val as object)) return '[Circular]';
        seen.add(val as object);
      }
      return val;
    },
    indent,
  );
}
